/**
 * Offline Indicator Component
 * Displays connection status and sync progress
 */

import { useState, useEffect } from 'react';
import { WifiOff, Wifi, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { backgroundSync, SyncStatus, SyncResult } from '@/lib/backgroundSync';
import { cn } from '@/lib/utils';

export const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Handle online/offline events
    const handleOnline = () => {
      console.log('Network: Online');
      setIsOnline(true);
    };

    const handleOffline = () => {
      console.log('Network: Offline');
      setIsOnline(false);
      setSyncStatus('idle');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Subscribe to sync status changes
    const unsubscribe = backgroundSync.onSyncStatusChange((status, result) => {
      setSyncStatus(status);
      if (result) {
        setSyncResult(result);
        
        // Auto-hide success message after 5 seconds
        if (status === 'success') {
          setTimeout(() => {
            setSyncStatus('idle');
            setSyncResult(null);
          }, 5000);
        }
      }
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
    };
  }, []);

  // Don't show anything if online and not syncing
  if (isOnline && syncStatus === 'idle') {
    return null;
  }

  const getStatusConfig = () => {
    if (!isOnline) {
      return {
        icon: WifiOff,
        text: 'You are offline',
        subtext: 'Changes will sync when connection is restored',
        bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
        borderColor: 'border-yellow-200 dark:border-yellow-800',
        textColor: 'text-yellow-800 dark:text-yellow-200',
        iconColor: 'text-yellow-600 dark:text-yellow-400'
      };
    }

    switch (syncStatus) {
      case 'syncing':
        return {
          icon: RefreshCw,
          text: 'Syncing changes...',
          subtext: 'Please wait while we sync your offline changes',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800',
          textColor: 'text-blue-800 dark:text-blue-200',
          iconColor: 'text-blue-600 dark:text-blue-400',
          animate: true
        };
      
      case 'success':
        return {
          icon: CheckCircle,
          text: 'All changes synced',
          subtext: syncResult ? `${syncResult.syncedCount} item(s) synced successfully` : '',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800',
          textColor: 'text-green-800 dark:text-green-200',
          iconColor: 'text-green-600 dark:text-green-400'
        };
      
      case 'error':
        return {
          icon: AlertCircle,
          text: 'Sync failed',
          subtext: syncResult ? `${syncResult.failedCount} item(s) failed to sync` : 'Some changes could not be synced',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
          textColor: 'text-red-800 dark:text-red-200',
          iconColor: 'text-red-600 dark:text-red-400'
        };
      
      default:
        return {
          icon: Wifi,
          text: 'Connected',
          subtext: '',
          bgColor: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-800',
          textColor: 'text-gray-800 dark:text-gray-200',
          iconColor: 'text-gray-600 dark:text-gray-400'
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  const handleRetry = async () => {
    if (isOnline && syncStatus === 'error') {
      await backgroundSync.retryFailed();
    }
  };

  return (
    <div
      className={cn(
        'fixed top-16 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4',
        'rounded-lg border shadow-lg p-4',
        'transition-all duration-300 ease-in-out',
        config.bgColor,
        config.borderColor
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <Icon
          className={cn(
            'w-5 h-5 flex-shrink-0 mt-0.5',
            config.iconColor,
            config.animate && 'animate-spin'
          )}
          aria-hidden="true"
        />
        
        <div className="flex-1 min-w-0">
          <p className={cn('font-medium text-sm', config.textColor)}>
            {config.text}
          </p>
          
          {config.subtext && (
            <p className={cn('text-xs mt-1 opacity-80', config.textColor)}>
              {config.subtext}
            </p>
          )}

          {/* Show details for errors */}
          {syncStatus === 'error' && syncResult && syncResult.errors.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className={cn(
                  'text-xs underline hover:no-underline',
                  config.textColor
                )}
              >
                {showDetails ? 'Hide details' : 'Show details'}
              </button>
              
              {showDetails && (
                <div className="mt-2 space-y-1">
                  {syncResult.errors.slice(0, 3).map((error, index) => (
                    <p key={index} className={cn('text-xs', config.textColor)}>
                      • {error.error}
                    </p>
                  ))}
                  {syncResult.errors.length > 3 && (
                    <p className={cn('text-xs', config.textColor)}>
                      ... and {syncResult.errors.length - 3} more
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Retry button for errors */}
          {syncStatus === 'error' && isOnline && (
            <button
              onClick={handleRetry}
              className={cn(
                'mt-2 text-xs font-medium px-3 py-1 rounded',
                'bg-white dark:bg-gray-800 border',
                config.borderColor,
                config.textColor,
                'hover:opacity-80 transition-opacity'
              )}
            >
              Retry sync
            </button>
          )}
        </div>

        {/* Close button for success/error states */}
        {(syncStatus === 'success' || syncStatus === 'error') && (
          <button
            onClick={() => {
              setSyncStatus('idle');
              setSyncResult(null);
            }}
            className={cn(
              'flex-shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/5',
              config.textColor
            )}
            aria-label="Close"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};
